<script setup lang="ts">
import PageTitle from '@/components/page-title.vue'
import PageNavigation from '@/components/page-navigation.vue'
import PageContent from '@/components/page-content.vue'
import { computed, ref, type UnwrapRef } from 'vue'
import ModalContainer from '@/components/modal/modal-container.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import ModalContent from '@/components/modal/modal-content.vue'
import { faCheck, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'
import ModalFooter from '@/components/modal/modal-footer.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import LinkText from '@/shared/components/LinkText.vue'
import AssetYieldsStatus from '@/features/assets/components/AssetYieldsStatus.vue'
import { AssetType, AssetDeliveryStatus } from '@/features/assets/constants'
import { useQuery } from '@tanstack/vue-query'
import { AssetQueries } from '@/features/assets/queries'
import { useAuthenticatedUser } from '@/shared/composables/useAuthenticatedUser'

const props = defineProps<{ assetCode: string }>()

const { data: assetDetails } = useQuery(AssetQueries.fetch(() => props.assetCode))
const { hasPermission } = useAuthenticatedUser()

const showPopup = ref(false)

const linkStatus = computed(
  () => ({
    meters: {
      available: assetDetails.value?.type === AssetType.Solar,
      disabled: !assetDetails.value?.setupAllowed.includes('link-meters')
    },
    settings: {
      available: !!assetDetails.value,
      disabled: !assetDetails.value?.setupAllowed.includes('settings')
    },
    yields: {
      available: assetDetails.value?.type === AssetType.Solar,
      disabled: !assetDetails.value?.setupAllowed.includes('yields')
    },
    invoice: {
      available: !!assetDetails.value,
      disabled: !assetDetails.value?.setupAllowed.includes('manage-invoice')
    }
  })
)

function showProcessPopup(linkKey: keyof UnwrapRef<typeof linkStatus>) {
  if (linkStatus.value[linkKey].available && linkStatus.value[linkKey].disabled) {
    showPopup.value = true
  }
}

</script>

<template>
  <PageContent>
    <PageTitle v-if="assetDetails">
      {{ assetDetails?.projectName }}

      <template #subtitle>(Project: {{ assetDetails.projectCode }} | Asset: {{ assetDetails.code }})</template>
    </PageTitle>

    <div class="d-flex justify-content-between align-items-center">
      <PageNavigation>
        <LinkText :to="{ name: 'assets.meters', params: { assetCode } }"
                  :class="{ [$style.disabled]: !linkStatus.meters.available }"
                  @click="showProcessPopup('meters')"
                  :disabled="linkStatus.meters.disabled">
          Link Meters
        </LinkText>
        <LinkText :to="{ name: 'assets.settings', params: { assetCode } }"
                  :class="{ [$style.disabled]: !linkStatus.settings.available }"
                  @click="showProcessPopup('settings')"
                  :disabled="linkStatus.settings.disabled">
          Settings
        </LinkText>
        <LinkText :to="{ name: 'assets.yields', params: { assetCode } }"
                  :class="{ [$style.disabled]: !linkStatus.yields.available }"
                  @click="showProcessPopup('yields')"
                  :disabled="linkStatus.yields.disabled">
          Yields
        </LinkText>
        <LinkText :to="{ name: 'assets.setupInvoice', params: { assetCode } }"
                  :class="{ [$style.disabled]: !linkStatus.invoice.available }"
                  @click="showProcessPopup('invoice')"
                  :disabled="linkStatus.invoice.disabled">
          Manage Invoice
        </LinkText>
      </PageNavigation>

      <AssetYieldsStatus v-if="$route.name === 'assets.yields'" :asset-code="assetCode"  />
    </div>

    <ModalContainer v-if="showPopup">
      <ModalTitle>Ensure all required info is provided</ModalTitle>
      <ModalContent :icon="faTriangleExclamation" icon-variant="warning">
        <template v-if="assetDetails?.type === AssetType.Solar">
          <p>The following should please be completed to proceed:</p>
          <p>
            <b>STEP 1:</b> Link meters associated with this asset.
            <br>
            <b>STEP 2:</b> Complete all inputs on the Settings tab.
            <br>
            <b>STEP 3:</b> Set up the Invoice Template with all recurring charges.
          </p>

          <p>
            If meters were linked, they should auto-fill the Invoice Template. Review the displayed
            information to confirm accuracy for future invoices.
          </p>
        </template>

        <template v-if="assetDetails?.type === AssetType.Wheeling">
          <p>Please complete all inputs on the settings tab before proceeding.</p>
        </template>
      </ModalContent>
      <ModalFooter class="text-end">
        <ButtonPrimary :icon="faCheck" @click="showPopup = false">Okay</ButtonPrimary>
      </ModalFooter>
    </ModalContainer>

    <RouterView />
  </PageContent>
</template>

<style module type="scss">
.disabled {
  cursor: not-allowed;
}
</style>
